import { useSearchParams } from "react-router-dom";
import type { ProductListFacets } from "@/lib/api-client";

export interface ActiveFiltersProps {
  facets: ProductListFacets;
}

interface Pill {
  key: string;
  label: string;
  remove: (params: URLSearchParams) => void;
}

/**
 * Porté depuis apps/web/components/product/ActiveFilters.tsx. Seul
 * changement : `usePathname`/`useRouter` de next/navigation →
 * `useSearchParams()` de react-router-dom.
 */
export function ActiveFilters({ facets }: ActiveFiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const pills: Pill[] = [];

  const brandSlugs = (searchParams.get("brand") ?? "").split(",").filter(Boolean);
  for (const slug of brandSlugs) {
    const brand = facets.brands.find((b) => b.slug === slug);
    pills.push({
      key: `brand:${slug}`,
      label: brand?.name ?? slug,
      remove: (params) => {
        const remaining = brandSlugs.filter((s) => s !== slug);
        if (remaining.length > 0) params.set("brand", remaining.join(","));
        else params.delete("brand");
      },
    });
  }

  const attributeTokens = searchParams.getAll("attribute");
  for (const token of attributeTokens) {
    const [code, normalizedValue] = token.split(":");
    const group = facets.attributes.find((a) => a.code === code);
    const value = group?.values.find((v) => v.normalizedValue === normalizedValue);
    pills.push({
      key: `attribute:${token}`,
      label: value ? `${group?.label ?? code} : ${value.label}` : token,
      remove: (params) => {
        params.delete("attribute");
        for (const t of attributeTokens) {
          if (t !== token) params.append("attribute", t);
        }
      },
    });
  }

  const priceMin = searchParams.get("priceMin");
  const priceMax = searchParams.get("priceMax");
  if (priceMin || priceMax) {
    pills.push({
      key: "price",
      label: `Prix : ${priceMin ?? "0"} – ${priceMax ?? "∞"} FCFA`,
      remove: (params) => {
        params.delete("priceMin");
        params.delete("priceMax");
      },
    });
  }

  const rating = searchParams.get("rating");
  if (rating) {
    pills.push({
      key: "rating",
      label: `${rating}★ & plus`,
      remove: (params) => params.delete("rating"),
    });
  }

  if (searchParams.get("inStock") === "true") {
    pills.push({
      key: "inStock",
      label: "En stock uniquement",
      remove: (params) => params.delete("inStock"),
    });
  }

  if (pills.length === 0) return null;

  function removePill(pill: Pill) {
    const params = new URLSearchParams(searchParams);
    pill.remove(params);
    setSearchParams(params);
  }

  return (
    <div className="flex flex-wrap gap-2 py-2">
      {pills.map((pill) => (
        <button
          key={pill.key}
          type="button"
          onClick={() => removePill(pill)}
          className="flex items-center gap-1.5 rounded-full border border-navy bg-white px-3 py-1 font-body text-xs text-navy hover:bg-navy hover:text-cream"
        >
          {pill.label}
          <span aria-hidden="true">&times;</span>
        </button>
      ))}
    </div>
  );
}
